import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchTaskById } from "./taskService";
import SubTasksTable from "./SubTasksTable";
import TaskFileAttachments from "./TaskFileAttachments";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/ViewCustomer.css";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/Tasks.css";

function ViewTask({ onBack }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId, taskId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEditTask = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!taskId) return;
      setLoading(true);
      setError("");
      try {
        const data = await fetchTaskById(taskId);
        setTask(data || null);
        if (!data) setError("Task not found.");
      } catch (err) {
        console.error("Error fetching task:", err);
        setError(err.response?.data?.message || "Failed to load task.");
        setTask(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [taskId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/view`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/view`);
    }
  };

  const handleEdit = () => {
    if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/tasks/${taskId}/edit`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/tasks/${taskId}/edit`);
    }
  };

  const refreshTask = useCallback(async () => {
    if (!taskId) return;
    try {
      const data = await fetchTaskById(taskId);
      if (data) setTask(data);
    } catch (err) {
      console.error("Error refetching task:", err);
    }
  }, [taskId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value != null && value !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading task...</div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="viewlead-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div
          className="error-message"
          style={{ backgroundColor: "#fee", color: "#c33", padding: "12px", borderRadius: "4px", marginTop: "16px" }}
        >
          {error || "Task not found."}
        </div>
      </div>
    );
  }

  const t = task;
  const priority = t.Priority ?? t.priority ?? "";
  const status = t.Status ?? t.status ?? "";
  const taskTitle = t.Title || t.title || "";
  const storyTitle = t.Story_Title || t.story_title || t.User_Story_Title || "";
  const projectName = t.Project_Name || t.project_name || "";
  const subProjectName = t.Sub_Project_Name || t.sub_project_name || "";
  const storyIdFromTask = t.Story_Id ?? t.story_id ?? userStoryId;

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" className="btn-subproject-primary" onClick={handleEdit}>
          Edit Task
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "nowrap",
          marginBottom: 20
        }}
      >
        <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
          <h3 className="viewlead-section-title">Task Information</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Task ID" value={String(t.Task_Id ?? t.task_id ?? "")} />
            <InfoItem label="Title" value={t.Title || t.title} />
            <InfoItem label="Status" value={status} />
            <InfoItem label="Priority" value={priority || "Not Set"} />
            <InfoItem label="Start Date" value={formatDate(t.Start_Date || t.start_date)} />
            <InfoItem label="End Date" value={formatDate(t.End_Date || t.end_date)} />
            <InfoItem label="Project" value={t.Project_Name || t.project_name} />
            <InfoItem label="Subproject" value={t.Sub_Project_Name || t.sub_project_name} />
            <InfoItem label="User Story" value={t.Story_Title || t.story_title || t.User_Story_Title} />
          </div>

          <h3 className="viewlead-section-title">Description</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Description" value={t.Description || t.description} />
          </div>
        </div>

        <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}>
          <h3 className="viewlead-section-title">Assigned Users</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {(t.Assigned_Users || t.assigned_users || []).length > 0 ? (
              (t.Assigned_Users || t.assigned_users || []).map((u, idx) => {
                const name = u.User_Name ?? u.user_name ?? u.name ?? "—";
                return (
                  <div key={u.User_Id ?? u.user_id ?? idx} className="viewlead-info-item">
                    <span className="viewlead-info-value">{name}</span>
                  </div>
                );
              })
            ) : (
              <div className="viewlead-info-item">
                <span className="viewlead-info-value muted">—</span>
              </div>
            )}
          </div>
          <h3 className="viewlead-section-title">Audit</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Created By" value={t.Created_By_Name || t.created_by_name} />
            <InfoItem label="Created On" value={formatDate(t.Created_On || t.created_on)} />
            <InfoItem label="Updated By" value={t.Updated_By_Name || t.updated_by_name} />
            <InfoItem label="Updated On" value={formatDate(t.Updated_On || t.updated_on)} />
          </div>
        </div>
      </div>

      <div className="viewlead-card issue-attachments-card project-attachments-card">
        <TaskFileAttachments
          task={t}
          taskId={taskId}
          canUpload={canEditTask}
          title="Task attachments"
          onRefresh={refreshTask}
        />
      </div>

      {/* Subtasks table - under task details */}
      <SubTasksTable
        taskId={Number(taskId)}
        storyId={storyIdFromTask ?? userStoryId}
        projectId={projectId}
        subProjectId={subprojectId}
        taskTitle={taskTitle}
        storyTitle={storyTitle}
        projectName={projectName}
        subProjectName={subProjectName}
        onRefresh={() => {}}
      />
    </div>
  );
}

export default ViewTask;

